import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import { RelatedMatterIndicator } from "./RelatedMatterIndicator";
import { CompactStatuteDateCell } from "@/components/features/matters/CompactStatuteDateCell";
import { MatterDescriptionCell } from "@/components/features/matters/MatterDescriptionCell";
import {
  formatInsuranceCoverageValue,
  formatInsuranceLiabilityValue,
  MatterInsuranceReportBadge,
} from "@/components/features/matters/MatterInsuranceReportBadge";
import {
  CurrencyWrapper,
  DataTableColumnHeader,
  TableBadge,
} from "@/components/ui";
import { formatTableColumnLabel } from "@/components/ui/shadcn/DataTable/formatTableColumnLabel";
import { type MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import {
  formatAddress,
  formatDate,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { matterLabelChipClassName } from "@/lib/utils/matters/matter-label-chip-class-name";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { type ColumnDef } from "@tanstack/react-table";

export type MattersTableColumnHeadersMode = "dataTable" | "plain";
export type MattersTableColumnsVariant = "default" | "massTort";

const MASS_TORT_EXCLUDED_COLUMN_IDS = [
  "Client",
  "Defendant(s)",
  "Insurance Claim Number",
  "Insurance Coverage",
  "Insurance Liability",
  "Insurance Policy Number",
  "Total Costs",
  "Medical Treatment Bill Total",
  "Tags",
  "Teams",
  "Assignees",
] as const;

const getMatterAddress = (row: MatterSearchResult) =>
  formatAddress({
    street1: row.incidentAddressStreet1 ?? undefined,
    street2: row.incidentAddressStreet2 ?? undefined,
    city: row.incidentAddressCity ?? "",
    state: row.incidentAddressState ?? "",
    zipCode: row.incidentAddressZip ?? undefined,
    country: row.incidentAddressCountry ?? undefined,
  });

const getClientNames = (row: MatterSearchResult) =>
  (row.clients ?? [])
    .map((client) => client.name?.trim())
    .filter((name): name is string => Boolean(name));

const getClientEmails = (row: MatterSearchResult) =>
  (row.clients ?? []).flatMap((client) => client.emailAddresses ?? []);

const getClientPhones = (row: MatterSearchResult) =>
  (row.clients ?? []).flatMap(
    (client) =>
      client.phoneNumbers?.map((phone) =>
        formatLocalOrIntlPhoneNumber(phone),
      ) ?? [],
  );

const getReferredByNames = (row: MatterSearchResult) =>
  row.referredByNames ?? [];

const getReferredToNames = (row: MatterSearchResult) =>
  row.referredToNames ?? [];

const toNumberOrUndefined = (value?: string | number | null) => {
  if (value == null) return undefined;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toInsuranceCoverageBadgeItems = (row: MatterSearchResult) =>
  (row.insuranceCoverages ?? []).map((item) => ({
    matterContactConnectionContactName: item.contactName,
    coverage: item.coverage,
    matterInsuranceId: item.insuranceId,
  }));

const toInsuranceLiabilityBadgeItems = (row: MatterSearchResult) =>
  (row.insuranceLiabilities ?? []).map((item) => ({
    matterContactConnectionContactName: item.contactName,
    matterInsuranceId: item.insuranceId,
    liability: item.liability,
    liabilityPercentage: toNumberOrUndefined(item.liabilityPercentage),
  }));

const getAssigneeNames = (row: MatterSearchResult) =>
  (row.assignees ?? [])
    .filter((assignee) => !assignee.dateTimeEnd)
    .map((assignee) =>
      `${assignee.user.firstName} ${assignee.user.lastName}`.trim(),
    )
    .filter(Boolean);

const normalizeDateSortValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const getPrimaryStatute = (row: MatterSearchResult) =>
  resolveReportPrimaryStatute(
    row.statutesOfLimitations?.map((statute) => ({
      ...statute,
      date: statute.date,
      description: statute.description ?? undefined,
    })) ?? [],
  );

export const getMattersTableColumns = (options?: {
  showRelatedMatters?: boolean;
  columnHeaders?: MattersTableColumnHeadersMode;
  includeDroppedDate?: boolean;
  includeReferredBy?: boolean;
  includeReferredTo?: boolean;
  variant?: MattersTableColumnsVariant;
  statusLabelsById?: Map<string, string>;
  typeLabelsById?: Map<string, string>;
}) => {
  const columnHeaders = options?.columnHeaders ?? "dataTable";
  const variant = options?.variant ?? "default";
  const mattersTableColumns: ColumnDef<MatterSearchResult>[] = [
    {
      accessorFn: (row) => normalizeDateSortValue(row.dateCreated),
      id: "Date Created",
      size: 150,
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue() as string | null;
        return value ? (
          <div>{formatDate(convertToLocalFromDatabaseDateTime(value))}</div>
        ) : (
          "-"
        );
      },
    },
    {
      accessorFn: (row) => normalizeDateSortValue(row.dateModified),
      id: "Date Modified",
      size: 150,
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue() as string | null;
        return value ? (
          <div>{formatDate(convertToLocalFromDatabaseDateTime(value))}</div>
        ) : (
          "-"
        );
      },
    },
    {
      accessorFn: (row) => row.title,
      id: "Title",
      size: 300,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableHiding: false,
      cell: ({ getValue, row }) => {
        const title = getValue() as string | null | undefined;
        return (
          <div className="flex items-center gap-2">
            <span className="font-semibold">{title?.trim() ? title : "-"}</span>
            {options?.showRelatedMatters && (
              <RelatedMatterIndicator matters={row.original.relatedMatters} />
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.incidentDescription?.trim() ?? "",
      id: "Incident Description",
      size: 350,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MatterDescriptionCell value={row.original.incidentDescription} />
      ),
    },
    {
      accessorFn: (row) => row.clientInjuryDescription?.trim() ?? "",
      id: "Injury Description",
      size: 350,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MatterDescriptionCell value={row.original.clientInjuryDescription} />
      ),
    },
    {
      accessorFn: (row) => getClientNames(row).join(", "),
      id: "Client",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => (row.getValue() as string) || "-",
    },
    {
      accessorFn: (row) => getClientEmails(row).join(", "),
      size: 175,
      id: "Client Email",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={getClientEmails(row.original)} />
      ),
    },
    {
      accessorFn: (row) => getClientPhones(row).join(", "),
      size: 175,
      id: "Client Phone",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={getClientPhones(row.original)} />
      ),
    },
    ...(options?.includeReferredBy
      ? ([
          {
            accessorFn: (row) => getReferredByNames(row).join(", "),
            id: "Referred By",
            size: 225,
            header: ({ column }) => <DataTableColumnHeader column={column} />,
            cell: ({ row }) => (
              <MultiValueCell value={getReferredByNames(row.original)} />
            ),
          },
        ] satisfies ColumnDef<MatterSearchResult>[])
      : []),
    ...(options?.includeReferredTo
      ? ([
          {
            accessorFn: (row) => getReferredToNames(row).join(", "),
            id: "Referred To",
            size: 225,
            header: ({ column }) => <DataTableColumnHeader column={column} />,
            cell: ({ row }) => (
              <MultiValueCell value={getReferredToNames(row.original)} />
            ),
          },
        ] satisfies ColumnDef<MatterSearchResult>[])
      : []),
    {
      accessorFn: (row) =>
        row.status?.label ??
        (row.statusId
          ? (options?.statusLabelsById?.get(row.statusId) ?? row.statusId)
          : null),
      id: "Status",
      size: 225,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const status = (row.getValue() as string | null)?.trim();
        if (!status) return "-";
        return (
          <TableBadge truncate className={matterLabelChipClassName(status)}>
            {status}
          </TableBadge>
        );
      },
    },
    {
      accessorFn: (row) => (row.defendantNames ?? []).join(", "),
      size: 175,
      id: "Defendant(s)",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={row.original.defendantNames ?? []} />
      ),
    },
    {
      accessorFn: (row) => (row.insuranceClaimNumbers ?? []).join(", "),
      id: "Insurance Claim Number",
      size: 190,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={row.original.insuranceClaimNumbers ?? []} />
      ),
    },
    {
      accessorFn: (row) => (row.insurancePolicyNumbers ?? []).join(", "),
      id: "Insurance Policy Number",
      size: 200,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={row.original.insurancePolicyNumbers ?? []} />
      ),
    },
    {
      accessorFn: (row) =>
        toInsuranceCoverageBadgeItems(row)
          .map(formatInsuranceCoverageValue)
          .join(", "),
      id: "Insurance Coverage",
      size: 220,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MatterInsuranceReportBadge
          type="coverage"
          items={toInsuranceCoverageBadgeItems(row.original)}
        />
      ),
    },
    {
      accessorFn: (row) =>
        toInsuranceLiabilityBadgeItems(row)
          .map(formatInsuranceLiabilityValue)
          .join(", "),
      id: "Insurance Liability",
      size: 265,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MatterInsuranceReportBadge
          type="liability"
          items={toInsuranceLiabilityBadgeItems(row.original)}
        />
      ),
    },
    {
      accessorFn: (row) => row.emailAddress,
      id: "Matter Email",
      size: 300,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const email = row.getValue() as string | null | undefined;
        return email ? email : "-";
      },
    },
    {
      accessorFn: (row) => toNumberOrUndefined(row.expenseTotal),
      id: "Total Costs",
      size: 140,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <CurrencyWrapper
          value={toNumberOrUndefined(row.original.expenseTotal)}
        />
      ),
      sortUndefined: "last",
    },
    {
      accessorFn: (row) => toNumberOrUndefined(row.medicalTreatmentBillTotal),
      id: "Medical Treatment Bill Total",
      size: 180,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <CurrencyWrapper
          value={toNumberOrUndefined(row.original.medicalTreatmentBillTotal)}
        />
      ),
      sortUndefined: "last",
    },
    {
      accessorFn: (row) => normalizeDateSortValue(row.dateOfLoss),
      id: "Date of Loss",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const dateOfLoss = row.getValue() as string | null;
        return dateOfLoss ? formatDate(dateOfLoss) : "-";
      },
    },
    {
      accessorFn: (row) =>
        row.incidentType?.label ??
        (row.incidentTypeId
          ? (options?.typeLabelsById?.get(row.incidentTypeId) ??
            row.incidentTypeId)
          : null),
      id: "Incident Type",
      size: 225,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const type = (row.getValue() as string | null)?.trim();
        if (!type) return "-";
        return (
          <TableBadge truncate className={matterLabelChipClassName(type)}>
            {type}
          </TableBadge>
        );
      },
    },
    {
      accessorFn: (row) =>
        (row.claimTypes ?? []).map((claim) => claim.label).join(", "),
      id: "Claim Types",
      size: 225,
      enableSorting: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <ClaimTypesCell claimTypes={row.original.claimTypes} />
      ),
    },
    {
      accessorFn: (row) => getMatterAddress(row),
      id: "Incident Address",
      size: 400,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => getMatterAddress(row.original) || "-",
    },
    {
      accessorFn: (row) => (row.tags ?? []).join(", "),
      id: "Tags",
      size: 180,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => <MultiValueCell value={row.original.tags ?? []} />,
    },
    {
      accessorFn: (row) => (row.teams ?? []).join(", "),
      id: "Teams",
      size: 180,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => <MultiValueCell value={row.original.teams ?? []} />,
    },
    {
      accessorFn: (row) => getAssigneeNames(row).join(", "),
      id: "Assignees",
      size: 225,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell value={getAssigneeNames(row.original)} />
      ),
    },
    {
      accessorFn: (row) =>
        normalizeDateSortValue(
          getPrimaryStatute(row).matterStatuteOfLimitations,
        ),
      id: "Statute of Limitations",
      size: 175,
      sortDescFirst: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const statute = getPrimaryStatute(row.row.original);

        return (
          <CompactStatuteDateCell
            statuteDate={statute.matterStatuteOfLimitations}
            confirmed={statute.matterStatuteOfLimitationsConfirmed}
          />
        );
      },
    },
  ];

  const removeColumn = (columnId: string) => {
    const columnIndex = mattersTableColumns.findIndex(
      (column) => column.id === columnId,
    );
    if (columnIndex >= 0) {
      mattersTableColumns.splice(columnIndex, 1);
    }
  };

  if (options?.includeDroppedDate) {
    const statusColumnIndex = mattersTableColumns.findIndex(
      (column) => column.id === "Status",
    );
    mattersTableColumns.splice(statusColumnIndex + 1, 0, {
      accessorFn: (row) => row.statusStartedAt,
      id: "Dropped Date",
      size: 150,
      enableSorting: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue() as string | null | undefined;
        return formatDate(value) ?? "-";
      },
    });
  }

  if (variant === "massTort") {
    MASS_TORT_EXCLUDED_COLUMN_IDS.forEach((columnId) => {
      removeColumn(columnId);
    });
  }

  if (columnHeaders === "plain") {
    return mattersTableColumns.map((col) => ({
      ...col,
      header: formatTableColumnLabel(col.id as string),
    }));
  }

  return mattersTableColumns;
};
